/**
 * @file pages/CountryGuide.jsx
 * @description Country travel guide page. Dynamically renders detailed
 * destination info, attractions, tips, and galleries based on country code.
 */

import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../Components/Layouts/Header';
import NikabaFooter from '../Components/Layouts/Footer';
import { FaCalendarAlt, FaPassport, FaMapMarkedAlt, FaUtensils, FaBus, FaBed, FaWallet, FaShieldAlt, FaLandmark } from 'react-icons/fa';
import { countryGuides, getCountryTheme } from '../data/countryGuides';

const CountryGuide = () => {
  const { countryCode } = useParams();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const theme = getCountryTheme(countryCode);

  useEffect(() => {
    setLoading(true);
    try {
      const guideData = countryGuides[countryCode.toLowerCase()];
      if (guideData) {
        setGuide(guideData);
      } else {
        setGuide(null);
      }
    } catch (error) {
      console.error('Error loading guide:', error);
      setGuide(null);
    }
      setLoading(false);
  }, [countryCode]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 pt-24">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-xl text-gray-600">Loading...</div>
          </div>
        </div>
        <NikabaFooter />
      </div>
    );
  }

  if (!guide) {
    return (
      <div className="min-h-screen bg-gray-100 pt-24">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <h1 className="text-2xl font-bold mb-4">Country Guide Not Found</h1>
            <p className="text-gray-600 mb-6">Sorry, we don't have information for this country yet.</p>
            <Link
              to="/destinations"
              className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Destinations
            </Link>
          </div>
        </div>
        <NikabaFooter />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FaLandmark },
    { id: 'planning', label: 'Trip Planning', icon: FaCalendarAlt },
    { id: 'destinations', label: 'Destinations', icon: FaMapMarkedAlt },
    { id: 'visa', label: 'Visa Guide', icon: FaPassport },
    { id: 'practical', label: 'Practical Info', icon: FaWallet }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-[#FDFDFF] to-[#F0F0F5] pt-24">
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="relative mb-8 rounded-xl overflow-hidden">
          <div 
            className="absolute inset-0 opacity-90"
            style={{ 
              background: `linear-gradient(to right, ${theme.primary}, ${theme.secondary})`
            }}
          ></div>
          <div className="relative z-10 flex items-center justify-between p-8">
            <div className="text-white">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">{guide.name}</h1>
              <p className="text-xl opacity-90">{guide.overview}</p>
            </div>
            <img
              src={`https://flagcdn.com/w160/${countryCode}.png`}
              alt={`${guide.name} flag`}
              className="hidden md:block h-[80px] rounded-lg shadow-lg"
            />
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex overflow-x-auto mb-8 bg-white rounded-lg shadow-lg">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 px-6 py-3 text-sm font-medium transition-colors duration-200 flex items-center justify-center gap-2
                  ${activeTab === tab.id
                    ? 'border-b-2'
                    : 'text-gray-500 hover:text-gray-700'
                  }`}
                style={{ 
                  borderColor: activeTab === tab.id ? theme.primary : 'transparent',
                  color: activeTab === tab.id ? theme.primary : undefined
                }}
              >
                <Icon className="text-lg" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content Sections */}
        <div className="grid gap-6">
          {activeTab === 'overview' && (
            <>
              <section className="bg-white rounded-lg shadow-lg p-6">
                <h2 className="text-2xl font-semibold mb-4" style={{ color: theme.primary }}>
                  About {guide.name}
                </h2>
                <p className="text-gray-700 leading-relaxed">{guide.overview}</p>
              </section>

              <section className="bg-white rounded-lg shadow-lg p-6">
                <h2 className="text-2xl font-semibold mb-4" style={{ color: theme.primary }}>
                  Highlights
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {guide.highlights && guide.highlights.length > 0 && guide.highlights.map((highlight, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div 
                        className="flex-shrink-0 w-2 h-2 rounded-full"
                        style={{ backgroundColor: theme.secondary }}
                      ></div>
                      <p className="text-gray-700">{highlight.name || highlight}</p>
                    </div>
                  ))}
                </div>
              </section>
            </>
          )}

          {activeTab === 'planning' && (
            <div className="grid gap-6">
              <section className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center space-x-3 mb-6">
                  <FaCalendarAlt className="text-2xl" style={{ color: theme.secondary }} />
                  <h2 className="text-2xl font-semibold" style={{ color: theme.primary }}>
                    Best Time to Visit
                  </h2>
                </div>
                <div className="text-gray-700">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-semibold mb-2">Seasons:</h3>
                      <ul className="space-y-2">
                        <li><strong>Peak Season:</strong> {guide.bestTimeToVisit.peak}</li>
                        <li><strong>Shoulder Season:</strong> {guide.bestTimeToVisit.shoulder}</li>
                        <li><strong>Low Season:</strong> {guide.bestTimeToVisit.lowSeason}</li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Notes:</h3>
                      <p>{guide.bestTimeToVisit.notes}</p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center space-x-3 mb-6">
                  <FaUtensils className="text-2xl" style={{ color: theme.secondary }} />
                  <h2 className="text-2xl font-semibold" style={{ color: theme.primary }}>
                    Local Cuisine
                  </h2>
                </div>
                <div className="text-gray-700">
                  <div className="grid gap-6">
                    <div>
                      <h3 className="font-semibold mb-2">Must-Try Dishes:</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        {guide.cuisine?.mustTry && guide.cuisine.mustTry.length > 0 && guide.cuisine.mustTry.map((dish, index) => (
                          <div key={index}>
                            <p className="font-medium">{dish.name}</p>
                            <p className="text-sm text-gray-600">{dish.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Dining Tips:</h3>
                      <ul className="list-disc ml-6">
                        {guide.cuisine?.diningTips && guide.cuisine.diningTips.length > 0 && guide.cuisine.diningTips.map((tip, index) => (
                          <li key={index}>{tip}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              <section className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center space-x-3 mb-6">
                  <FaBus className="text-2xl" style={{ color: theme.secondary }} />
                  <h2 className="text-2xl font-semibold" style={{ color: theme.primary }}>
                    Transportation
                  </h2>
                </div>
                <div className="text-gray-700">
                  <div className="grid gap-6">
                    <div>
                      <h3 className="font-semibold mb-2">Public Transportation:</h3>
                      {guide.transportation?.public?.types && guide.transportation.public.types.length > 0 && guide.transportation.public.types.map((type, index) => (
                        <div key={index} className="mb-3">
                          <p className="font-medium">{type.name}</p>
                          <p className="text-sm text-gray-600">{type.description}</p>
                          <p className="text-sm text-gray-600">Cost: {type.cost}</p>
                        </div>
                      ))}
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Transportation Tips:</h3>
                      <ul className="list-disc ml-6">
                        {guide.transportation?.public?.tips && guide.transportation.public.tips.length > 0 && guide.transportation.public.tips.map((tip, index) => (
                          <li key={index}>{tip}</li>
                    ))}
                  </ul>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          )}

          {activeTab === 'destinations' && (
            <section className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center space-x-3 mb-6">
                <FaMapMarkedAlt className="text-2xl" style={{ color: theme.secondary }} />
                <h2 className="text-2xl font-semibold" style={{ color: theme.primary }}>
                  Popular Destinations
                </h2>
              </div>
              <div className="grid gap-6">
                {guide.popularDestinations && guide.popularDestinations.length > 0 && guide.popularDestinations.map((destination, index) => (
                  <div key={index} className="border-b last:border-b-0 pb-6 last:pb-0">
                    <h3 className="text-xl font-medium mb-2" style={{ color: theme.primary }}>
                      {destination.name}
                    </h3>
                    <p className="text-gray-700 mb-3">{destination.description}</p>
                    <div className="mt-2">
                      <strong>Highlights:</strong>
                      <ul className="list-disc ml-6 mt-1">
                        {destination.highlights && destination.highlights.length > 0 && destination.highlights.map((highlight, idx) => (
                          <li key={idx} className="text-gray-700">{highlight}</li>
                        ))}
                      </ul>
                    </div>
                    <p className="text-gray-600 mt-3"><strong>Best Time to Visit:</strong> {destination.bestTimeToVisit}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {activeTab === 'visa' && (
              <section className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center space-x-3 mb-6">
                <FaPassport className="text-2xl" style={{ color: theme.secondary }} />
                  <h2 className="text-2xl font-semibold" style={{ color: theme.primary }}>
                  Visa Requirements
                  </h2>
                </div>
                <div className="text-gray-700">
                <p className="mb-4">{guide.visaRequirements.general}</p>
                
                <h3 className="font-semibold mb-2">Requirements:</h3>
                <ul className="list-disc ml-6 mb-4">
                  {guide.visaRequirements?.requirements && guide.visaRequirements.requirements.length > 0 && guide.visaRequirements.requirements.map((req, index) => (
                    <li key={index}>{req}</li>
                    ))}
                  </ul>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <h3 className="font-semibold mb-2">Visa Fee:</h3>
                    <p>{guide.visaRequirements.fees}</p>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Processing Time:</h3>
                    <p>{guide.visaRequirements.processingTime}</p>
                  </div>
                </div>
                </div>
              </section>
          )}

          {activeTab === 'practical' && (
            <div className="grid md:grid-cols-2 gap-6">
              <section className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <FaBed className="text-2xl" style={{ color: theme.secondary }} />
                  <h2 className="text-2xl font-semibold" style={{ color: theme.primary }}>
                    Accommodation
                  </h2>
                </div>
                <div className="text-gray-700">
                  {guide.accommodation?.types && guide.accommodation.types.length > 0 && guide.accommodation.types.map((type, index) => (
                    <div key={index} className="mb-4">
                      <h3 className="font-semibold">{type.category}</h3>
                      <p>Price Range: {type.priceRange}</p>
                      <p>Examples: {type.examples.join(', ')}</p>
                    </div>
                  ))}
                  <h3 className="font-semibold mt-4 mb-2">Booking Tips:</h3>
                  <ul className="list-disc ml-6">
                    {guide.accommodation?.bookingTips && guide.accommodation.bookingTips.length > 0 && guide.accommodation.bookingTips.map((tip, index) => (
                      <li key={index}>{tip}</li>
                    ))}
                  </ul>
                </div>
              </section>

              <section className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <FaWallet className="text-2xl" style={{ color: theme.secondary }} />
                  <h2 className="text-2xl font-semibold" style={{ color: theme.primary }}>
                    Budget Information
                  </h2>
                </div>
                <div className="text-gray-700">
                  <h3 className="font-semibold mb-2">Daily Budget:</h3>
                  <ul className="list-none mb-4">
                    <li>Budget: {guide.budgetInfo.daily.budget}</li>
                    <li>Mid-Range: {guide.budgetInfo.daily.midRange}</li>
                    <li>Luxury: {guide.budgetInfo.daily.luxury}</li>
                  </ul>
                  <h3 className="font-semibold mb-2">Money Tips:</h3>
                  <ul className="list-disc ml-6">
                    {guide.budgetInfo?.moneyTips && guide.budgetInfo.moneyTips.length > 0 && guide.budgetInfo.moneyTips.map((tip, index) => (
                      <li key={index}>{tip}</li>
                    ))}
                  </ul>
                </div>
              </section>

              <section className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <FaShieldAlt className="text-2xl" style={{ color: theme.secondary }} />
                  <h2 className="text-2xl font-semibold" style={{ color: theme.primary }}>
                    Safety Tips
                  </h2>
                </div>
                <div className="text-gray-700">
                  <h3 className="font-semibold mb-2">General Safety:</h3>
                  <ul className="list-disc ml-6 mb-4">
                    {guide.safetyTips?.general && guide.safetyTips.general.length > 0 && guide.safetyTips.general.map((tip, index) => (
                      <li key={index}>{tip}</li>
                    ))}
                  </ul>
                  <h3 className="font-semibold mb-2">Health Precautions:</h3>
                  <ul className="list-disc ml-6">
                    {guide.safetyTips?.healthPrecautions && guide.safetyTips.healthPrecautions.length > 0 && guide.safetyTips.healthPrecautions.map((tip, index) => (
                      <li key={index}>{tip}</li>
                    ))}
                  </ul>
                </div>
              </section>
            </div>
          )}
        </div>
      </main>

      <NikabaFooter />
    </div>
  );
};

export default CountryGuide; 